import React, { useEffect, useState } from "react";
import Select from "react-select";
import Swal from "sweetalert2";
import { ROLL_CALL_API_BASE_URL } from "../RollCallConfig/RollCallConfig";
import PropTypes from "prop-types";
import { Spin } from "antd";

const EditLeave = ({ editObj, onUpdateSuccess, onClose, isOpen }) => {
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [selectedLeaveType, setSelectedLeaveType] = useState();
  const [halfDay, setHalfDay] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [comments, setComments] = useState("");
  const [tokenData, setTokenData] = useState(null);
  const [superIdData, setSuperIdData] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [editObjData, setEditObjData] = useState({});
  useEffect(() => {
    if (editObj) {
      setEditObjData(editObj);
    }
  }, [editObj]);

  useEffect(() => {
    if (editObj) {
      setEditObjData(editObj);

      setSelectedEmp({
        value: editObj?.RegId,
        label: editObj?.UserName,
      });

      setSelectedLeaveType({
        value: editObj.LvTypeId,
        label: editObj.LvType,
      });

      setStartDate(formatDateForInput(editObj.StartDt));
      setEndDate(formatDateForInput(editObj.EndDt));

      setComments(editObj.Comments || "");

      setHalfDay(editObj.LeaveCount === 0.5);
    }
  }, [editObj]);

  useEffect(() => {
    const token = sessionStorage.getItem("authToken");
    const superId = sessionStorage.getItem("superId");

    if (token && superId) {
      setTokenData(token);
      setSuperIdData(superId);
    } else {
      console.warn("User data not found in sessionStorage");
    }
  }, []);

  useEffect(() => {
    if (tokenData && superIdData) {
      const fetchLeaveTypes = async () => {
        try {
          const response = await fetch(
            `${ROLL_CALL_API_BASE_URL}portal/leave/getleavetype`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${tokenData}`,
              },
            },
          );

          if (!response.ok) throw new Error("Failed to fetch leave types");

          const data = await response.json();
          if (data.status) {
            const activeRecords = data.ResultData?.filter(
              (item) => item.IsActive,
            );
            setLeaveTypes(activeRecords);
          } else {
            Swal.fire({
              title: "Error",
              text: data.ErrorMsg || "Unable to fetch leave types",
              icon: "error",
            });
          }
        } catch (error) {
          console.error("Error fetching leave types:", error);
        }
      };

      fetchLeaveTypes();
    }
  }, [superIdData, tokenData]);

  const leaveTypeOptions = leaveTypes?.map((type) => ({
    value: type.Id,
    label: type.LvType,
  }));

  const calculateLeaveCount = (start, end, halfDay) => {
    const startDateObj = new Date(start);
    const endDateObj = new Date(end);
    const daysDifference =
      Math.ceil((endDateObj - startDateObj) / (1000 * 60 * 60 * 24)) + 1;
    return halfDay ? daysDifference - 0.5 : daysDifference;
  };

  const formatDateForInput = (dateStr) =>
    dateStr ? dateStr.split("T")[0] : "";

  const handleLeaveSubmit = async (event) => {
    event.preventDefault();
    setSubmitLoading(true);

    if (!selectedLeaveType || !startDate || !endDate) {
      Swal.fire("Error", "All required fields are mandatory", "error");
      setSubmitLoading(false);
      return;
    }

    if (halfDay && startDate !== endDate) {
      Swal.fire(
        "Error",
        "For Half-Day leave, Start Date and End Date must be the same",
        "error",
      );
      setSubmitLoading(false);
      return;
    }

    const leaveCount = calculateLeaveCount(startDate, endDate, halfDay);

    const leaveData = {
      inputdata: {
        Id: editObjData.Id,
        superid: superIdData,
        regid: selectedEmp.value,
        lvtypeid: selectedLeaveType.value,
        halfday: halfDay ? 1 : 0,
        startdate: startDate,
        enddate: endDate,
        leavecount: leaveCount,
        comments: comments,
      },
    };

    try {
      const response = await fetch(
        `${ROLL_CALL_API_BASE_URL}portal/leave/updateleave`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${tokenData}`,
          },
          body: JSON.stringify(leaveData),
        },
      );

      const result = await response.json();

      if (result.status) {
        const updatedLeave = {
          Id: editObjData.Id,
          RegId: selectedEmp.value,
          LeaveTypeId: selectedLeaveType.value,
          LeaveTypeName: selectedLeaveType.label,
          StartDate: startDate,
          EndDate: endDate,
          HalfDay: halfDay ? 1 : 0,
          LeaveCount: leaveCount,
          Comments: comments || "No Comments",
        };
        Swal.fire("Success", "Leave Updated Successfully", "success").then(
          () => {
            onUpdateSuccess(updatedLeave);
            onClose();
          },
        );
      } else {
        Swal.fire("Error", result.Message, "error");
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Something went wrong", "error");
    } finally {
      setSubmitLoading(false);
    }
  };

  useEffect(() => {
    const offcanvasEl = document.getElementById("offcanvasRightEditLeave");

    const handleOffcanvasClose = () => {
      // handlecancel();
    };

    if (offcanvasEl) {
      offcanvasEl.addEventListener("hidden.bs.offcanvas", handleOffcanvasClose);
    }

    return () => {
      if (offcanvasEl) {
        offcanvasEl.removeEventListener(
          "hidden.bs.offcanvas",
          handleOffcanvasClose,
        );
      }
    };
  }, []);

  const handleClose = () => {
    setEditObjData(editObj);
    setSelectedEmp(null);

    // Leave Type
    setSelectedLeaveType({
      value: editObj.LvTypeId,
      label: editObj.LvType,
    });

    // Dates
    setStartDate(formatDateForInput(editObj.StartDt));
    setEndDate(formatDateForInput(editObj.EndDt));

    // Comments
    setComments(editObj.Comments || "");

    // Half day (optional – if API provides)
    setHalfDay(editObj.LeaveCount === 0.5);
    onClose();
  };

  return (
    <>
      {isOpen && (
        <div>
          <style>
            {`
        @media (max-width: 576px) {
          #offcanvasRightEditLeave {
            width: 90% !important;
          }
        }

        @media (min-width: 577px) and (max-width: 992px) {
          #offcanvasRightEditLeave {
            width: 55% !important;
          }
        }

        @media (min-width: 993px) and (max-width: 1440px) {
          #offcanvasRightEditLeave {
            width: 35% !important;
          }
        }
          
       @media (min-width: 1441px) and (max-width:2550px) {
          #offcanvasRightEditLeave {
            width: 22% !important;
          }
        }
        @media (min-width:2551px) {
          #offcanvasRightEditLeave {
            width:18% !important;
          }
        }
        `}
          </style>
          {submitLoading && (
            <div
              className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
              style={{
                zIndex: 1050,
                backdropFilter: "blur(4px)",
                backgroundColor: "rgba(255, 255, 255, 0.2)",
              }}
            >
              <Spin size="large" tip="Processing..." />
            </div>
          )}

          {/* Backdrop */}
          <div className="offcanvas-backdrop fade show" onClick={onClose} />

          <div
            className="offcanvas offcanvas-end show"
            id="offcanvasRightEditLeave"
            style={{ visibility: "visible" }}
          >
            <div className="offcanvas-header">
              <h4>Edit Leave</h4>
              <button
                type="button"
                className="btn-close"
                onClick={handleClose}
                aria-label="Close"
              />
            </div>
            <hr />
            <div className="offcanvas-body">
              <form onSubmit={handleLeaveSubmit}>
                <div className="row">
                  <div className="col-6 mb-3">
                    <label className="form-label">
                      <span className="text-danger ms-1">*</span>Employee
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={selectedEmp?.label || ""}
                      disabled
                    />
                  </div>
                  <div className="col-6 mb-3">
                    <label className="form-label">
                      <span className="text-danger ms-1">*</span>Leave Type
                    </label>
                    <Select
                      placeholder="Leave Type"
                      value={selectedLeaveType}
                      onChange={setSelectedLeaveType}
                      options={leaveTypeOptions}
                    />
                  </div>
                  <div className="col-6 mb-3">
                    <label className="form-label">
                      <span className="text-danger ms-1">*</span>Start Date
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      required
                    />
                  </div>
                  <div className="col-6 mb-3">
                    <label className="form-label">
                      <span className="text-danger ms-1">*</span>End Date
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      required
                    />
                  </div>
                  <div className="col-6 mb-3">
                    <label className="form-label">Half-Day</label>
                    <input
                      type="checkbox"
                      className="form-check ms-2"
                      checked={halfDay}
                      onChange={(e) => setHalfDay(e.target.checked)}
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Reason</label>
                    <textarea
                      className="form-control"
                      value={comments}
                      onChange={(e) => setComments(e.target.value)}
                    />
                  </div>
                </div>
                <div className="d-flex justify-content-end mt-3">
                  <button
                    type="button"
                    className="btn btn-secondary me-2"
                    data-bs-dismiss="offcanvas"
                    onClick={handleClose}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-success">
                    Submit
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

EditLeave.propTypes = {
  editObj: PropTypes.object.isRequired,
  onUpdateSuccess: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  isOpen: PropTypes.func.isRequired,
};

export default EditLeave;
